import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, TrendingUp, CheckCircle, Clock, AlertTriangle, AlertOctagon,
  Shield, Loader2, Eye, Zap, MapPin, Activity, BarChart3, Building2,
  ArrowRight, Lock, ChevronDown, ChevronUp, RefreshCw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ExecutiveApprovalInsights } from '../components/DashboardApprovalInsights';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// --- KPI Card ---
const KPICard = ({ label, value, icon: Icon, color, sub, onClick }) => (
  <div onClick={onClick} className={`bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-all ${onClick ? 'cursor-pointer' : ''}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center justify-between mb-2">
      <div className={`w-9 h-9 rounded-lg bg-${color}-50 flex items-center justify-center`}>
        <Icon size={18} className={`text-${color}-500`} />
      </div>
      {onClick && <ArrowRight size={14} className="text-gray-300" />}
    </div>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    {sub && <p className="text-[10px] text-gray-400 mt-1">{sub}</p>}
  </div>
);

// --- Alert Card ---
const AlertCard = ({ label, count, severity, onClick }) => {
  const colors = { red: 'bg-red-50 border-red-200 text-red-700', amber: 'bg-amber-50 border-amber-200 text-amber-700', blue: 'bg-blue-50 border-blue-200 text-blue-700' };
  return (
    <div onClick={onClick} className={`rounded-xl px-4 py-3 border flex items-center justify-between ${colors[severity] || colors.blue} ${onClick ? 'cursor-pointer hover:shadow-sm' : ''} transition-all`} data-testid={`alert-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2">
        {severity === 'red' ? <AlertOctagon size={16} /> : <AlertTriangle size={16} />}
        <span className="text-sm font-medium">{label}</span>
      </div>
      <span className="text-lg font-bold">{count}</span>
    </div>
  );
};

// --- Pipeline Step ---
const PipelineStep = ({ label, count, active, isBottleneck }) => (
  <div className={`flex-1 text-center p-3 rounded-lg border-2 transition-all ${isBottleneck ? 'border-red-300 bg-red-50' : active ? 'border-teal-300 bg-teal-50' : 'border-gray-200 bg-gray-50'}`} data-testid={`pipeline-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <p className="text-xl font-bold text-gray-900">{count}</p>
    <p className={`text-[10px] font-medium ${isBottleneck ? 'text-red-600' : 'text-gray-500'}`}>{label}</p>
    {isBottleneck && <p className="text-[9px] text-red-500 font-semibold mt-0.5">BOTTLENECK</p>}
  </div>
);

// --- Bar (for geo/team) ---
const HorizBar = ({ label, value, max, color = 'teal' }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-gray-600 w-28 truncate" title={label}>{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-2.5">
        <div className={`h-2.5 rounded-full bg-${color}-500 transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-semibold text-gray-700 w-10 text-right">{value}</span>
    </div>
  );
};

// --- Collapsible Section ---
const Section = ({ title, icon: Icon, children, defaultOpen = true, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid={testId}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors">
        <span className="flex items-center gap-2 text-sm font-bold text-gray-800">
          {Icon && <Icon size={16} className="text-teal-600" />}
          {title}
        </span>
        {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>
      {open && <div className="px-5 pb-5 space-y-4">{children}</div>}
    </div>
  );
};

// --- Activity Feed Item ---
const FeedItem = ({ item, navigate }) => {
  const timeAgo = (ts) => {
    if (!ts) return '';
    try {
      const diff = (Date.now() - new Date(ts).getTime()) / 1000;
      if (diff < 60) return 'just now';
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      return `${Math.floor(diff / 86400)}d ago`;
    } catch { return ''; }
  };
  return (
    <div className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${item.type === 'audit' ? 'bg-blue-400' : 'bg-teal-400'}`} />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-700 truncate">{item.message}</p>
        <p className="text-[10px] text-gray-400">{timeAgo(item.time)}</p>
      </div>
      {item.lead_id && (
        <button onClick={() => navigate(`/leads/${item.lead_id}`)} className="text-gray-400 hover:text-teal-600 shrink-0"><Eye size={12} /></button>
      )}
    </div>
  );
};

const ExecutiveDashboard = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/dashboard/system-overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
    setRefreshing(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const refresh = () => { setRefreshing(true); fetchData(); };

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-teal-500" /></div>;
  if (!data) return <p className="text-sm text-gray-500 text-center py-8">Unable to load dashboard data.</p>;

  const k = data.kpi;
  const ar = data.action_required;
  const p = data.pipeline;
  const tp = data.team_performance;
  const geo = data.geographical;
  const maxCity = Math.max(...(geo.top_cities.map(c => c.count) || [1]), 1);
  const maxState = Math.max(...(geo.top_states.map(s => s.count) || [1]), 1);

  return (
    <div className="space-y-5" data-testid="system-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          <Shield size={20} className="text-teal-600" />
          {isAdmin ? 'Admin System Dashboard' : 'Executive Dashboard'}
        </h2>
        <button onClick={refresh} disabled={refreshing} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors" data-testid="refresh-dashboard">
          <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPICard label="Total Leads" value={k.total_leads.toLocaleString()} icon={Users} color="teal" />
        <KPICard label="Closed Won" value={k.closed_won} icon={CheckCircle} color="emerald" />
        <KPICard label="Conversion" value={`${k.conversion_rate}%`} icon={TrendingUp} color="blue" />
        <KPICard label="Active Properties" value={k.active_properties} icon={Building2} color="indigo" />
        <KPICard label="Onboarding" value={k.onboarding} icon={ArrowRight} color="amber" />
        <KPICard label="Offboarding" value={k.offboarding} icon={AlertTriangle} color="red" />
      </div>

      {/* Action Required */}
      <Section title="Action Required" icon={AlertOctagon} testId="action-required-section">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <AlertCard label="Pending L1 (GTM)" count={ar.pending_l1} severity="amber" onClick={() => navigate('/leads?approval_status=pending')} />
          <AlertCard label="Pending L2 (BFR)" count={ar.pending_l2} severity="amber" onClick={() => navigate('/leads?approval_status=l1_approved')} />
          <AlertCard label="Pending Final" count={ar.pending_final} severity="amber" onClick={() => navigate('/leads?stage=executive_pending')} />
          <AlertCard label="Pending Lead Verification" count={ar.pending_lead_verify} severity="blue" onClick={() => navigate('/leads?lead_stage=Closed Won')} />
          <AlertCard label="Critical Checklists" count={ar.critical_checklist} severity="red" onClick={() => navigate('/leads?checklist_status=critical')} />
          <AlertCard label="Urgent Checklists" count={ar.urgent_checklist} severity="amber" onClick={() => navigate('/leads?checklist_status=urgent')} />
        </div>
      </Section>

      {/* Commercial Approval Pipeline */}
      <Section title="Commercial Approval Pipeline" icon={BarChart3} testId="pipeline-section">
        <div className="flex gap-2 items-stretch">
          {[
            { key: 'not_started', label: 'Not Started' },
            { key: 'gtm_pending', label: 'GTM Pending' },
            { key: 'bfr_pending', label: 'BFR Pending' },
            { key: 'executive_pending', label: 'Exec Pending' },
            { key: 'verified', label: 'Verified' },
          ].map((stage, i, arr) => (
            <React.Fragment key={stage.key}>
              <PipelineStep label={stage.label} count={p[stage.key]} active={p[stage.key] > 0} isBottleneck={data.pipeline_bottleneck === stage.key} />
              {i < arr.length - 1 && <div className="flex items-center text-gray-300"><ArrowRight size={16} /></div>}
            </React.Fragment>
          ))}
        </div>
      </Section>

      {/* Team Performance */}
      <Section title="Team Performance" icon={Users} testId="team-performance-section">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-xs text-blue-600 font-medium">BFR Approved</p>
              <p className="text-xl font-bold text-blue-900">{tp.bfr.approved}</p>
            </div>
            <div className="bg-indigo-50 rounded-lg p-3">
              <p className="text-xs text-indigo-600 font-medium">Executive Approved</p>
              <p className="text-xl font-bold text-indigo-900">{tp.executive.approved}</p>
            </div>
          </div>
          <p className="text-xs font-semibold text-gray-600">GTM Sales Performance</p>
          {tp.gtm.slice(0, 5).map(m => (
            <div key={m.name} className="flex items-center justify-between text-xs">
              <span className="text-gray-700 truncate w-32">{m.name}</span>
              <span className="text-gray-500">{m.total} leads</span>
              <span className="text-gray-500">{m.closed_won} won</span>
              <span className={`font-semibold ${m.conversion > 1 ? 'text-emerald-600' : 'text-gray-400'}`}>{m.conversion}%</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Row: Geographical + Activity Feed */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Geographical Insights */}
        <Section title="Geographical Insights" icon={MapPin} testId="geo-section" defaultOpen={false}>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2">Top Cities</p>
              <div className="space-y-2">
                {geo.top_cities.map(c => <HorizBar key={c.name} label={c.name} value={c.count} max={maxCity} />)}
                {geo.top_cities.length === 0 && <p className="text-xs text-gray-400">No city data</p>}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2">Top States</p>
              <div className="space-y-2">
                {geo.top_states.map(s => <HorizBar key={s.name} label={s.name} value={s.count} max={maxState} color="blue" />)}
                {geo.top_states.length === 0 && <p className="text-xs text-gray-400">No state data</p>}
              </div>
            </div>
          </div>
        </Section>

        {/* Live Activity Feed */}
        <Section title="Live Activity Feed" icon={Activity} testId="activity-feed-section" defaultOpen={false}>
          <div className="max-h-64 overflow-y-auto">
            {data.activity_feed.map((item, i) => <FeedItem key={i} item={item} navigate={navigate} />)}
            {data.activity_feed.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No recent activity</p>}
          </div>
        </Section>
      </div>

      {/* Quick Actions */}
      <Section title="Quick Actions" icon={Zap} testId="quick-actions-section">
        <div className="flex flex-wrap gap-2">
          <button onClick={() => navigate('/leads?stage=Closed Won')} className="px-4 py-2 text-xs font-medium bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors" data-testid="quick-action-critical">
            <AlertOctagon size={13} className="inline mr-1" />View Critical Leads
          </button>
          <button onClick={() => navigate('/leads?stage=Closed Won')} className="px-4 py-2 text-xs font-medium bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors" data-testid="quick-action-approvals">
            <Clock size={13} className="inline mr-1" />View Pending Approvals
          </button>
          <button onClick={() => navigate('/leads')} className="px-4 py-2 text-xs font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors" data-testid="quick-action-sla">
            <AlertTriangle size={13} className="inline mr-1" />View SLA Issues
          </button>
          {isAdmin && (
            <button onClick={() => navigate('/settings/users')} className="px-4 py-2 text-xs font-medium bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors" data-testid="quick-action-override">
              <Lock size={13} className="inline mr-1" />Admin Override Access
            </button>
          )}
        </div>
      </Section>

      {/* Existing Approval Insights (preserved) */}
      <div className="border-t border-gray-200 pt-5">
        <ExecutiveApprovalInsights />
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
